import React, { useState, useEffect } from 'react';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Chart from 'react-google-charts';
import axios from 'axios';
import qs from 'qs';
import Spinner from 'react-bootstrap/Spinner';
import { LoadingAnimation, Icon } from '../../common/components/Utilities';

function WeeklyReport({ start, end }) {
  const squads = [
    { id: 1, name: 'Squad 1' },
    { id: 2, name: 'Squad 2' },
    { id: 3, name: 'Squad 3' },
    { id: 4, name: 'Squad 4' },
    { id: 5, name: 'Squad 5' },
    { id: 11, name: 'Squad 6' },
    { id: 14, name: 'Squad 7' },
    { id: 15, name: 'Platform 1' },
    { id: 6, name: 'Product Support' },
    { id: 8, name: 'Platform Architecture' },
    { id: 9, name: 'Platform Infrastructure' },
    { id: 13, name: 'Platform Reliability' },
    { id: 16, name: 'Quality Engineering' },
  ];

  const groups = [
    { id: 4, name: 'Software Engineers' },
    { id: 5, name: 'Project Managers' },
    { id: 6, name: 'Business Analysts' },
    { id: 7, name: 'Team Leads' },
    { id: 8, name: 'Quality Engineering' },
  ];

  return (
    <>
      <div className="row mb-4">
        <div className="col-md-9">
          <div className="card mb-4">
            <h3>Everyone</h3>
            <div className="card-body">
              <TimeChart start={start} end={end} showPeople={false} />
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card mb-4">
            <h3>Time by Role</h3>
            <div className="card-body">
              <ul>
                {groups.map((group) => (
                  <li key={`group-link-${group.id}`}>
                    <a href={`#group-${group.id}`}>{group.name}</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="card">
            <h3>Time by Squad</h3>
            <div className="card-body">
              <ul>
                {squads.map((squad) => (
                  <li key={`squad-link-${squad.id}`}>
                    <a href={`#squad-${squad.id}`}>{squad.name}</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="group-reports row">
        {groups.map((group) => <GroupWeeklyReport start={start} end={end} key={`group-report-${group.id}`} group={group} />)}
      </div>

      <div className="squad-reports row">
        {squads.map((squad) => <SquadWeeklyReport start={start} end={end} key={`squad-report-${squad.id}`} squad={squad} />)}
      </div>
    </>
  );
}

export function GroupWeeklyReport({ start, end, group }) {
  return (
    <div className="col-md-12" id={`group-${group.id}`}>
      <div className="group-report card mb-4">
        <h3>{group.name}</h3>
        <div className="card-body">
          <TimeChart start={start} end={end} group={group.id} showPeople />
        </div>
      </div>
    </div>
  );
}

export function SquadWeeklyReport({ start, end, squad }) {
  return (
    <div className="col-md-12" id={`squad-${squad.id}`}>
      <div className="squad-report card mb-4">
        <h3>{squad.name}</h3>
        <div className="card-body">
          <TimeChart start={start} end={end} squad={squad.id} showPeople />
        </div>
      </div>
    </div>
  );
}

export function TimeChart({
  user,
  squad,
  group,
  start,
  end,
  hideLegend,
  showDrilldown = true,
  showPeople = false,
  showTable = true,
}) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState();
  const [drilldown, setDrilldown] = useState();

  const loadData = async () => {
    setLoading(true);

    try {
      const response = await axios.get('/dash/time-data', {
        params: {
          user_id: user,
          squad_id: squad,
          group_id: group,
          start,
          end,
        },
        paramsSerializer: (params) => qs.stringify(params),
      });

      setData(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [squad, group, start, end]);

  if (!data || data.length === 0) {
    return (
      <div style={{ height: 400 }}>
        <LoadingAnimation loading={loading} />
      </div>
    );
  }

  const chartData = [
    ['Activity', 'Hours'],
    ['Tickets', data.current.percentages.tickets],
    ['Project Ceremonies', data.current.percentages.project_ceremonies],
    ['Meetings', data.current.percentages.meetings],
    ['Project Activity', data.current.percentages.other_activity],
    ['Untracked', data.current.percentages.untracked],
  ];

  const chartOptions = {
    pieHole: 0.2,
    chartArea: {
      left: 20,
      right: 20,
      top: 20,
      bottom: 20,
    },
    slices: {
      0: { color: '#38c172' },
      1: { color: '#4dc0b5' },
      2: { color: '#3490dc' },
      3: { color: '#85A7C4' },
      4: { color: '#cccccc' },
    },
  };

  if (hideLegend) {
    chartOptions.legend = 'none';
  }

  let chartEvents = [];
  let drilldownTable;

  if (showDrilldown) {
    chartEvents = [
      {
        eventName: 'select',
        callback: ({ chartWrapper }) => {
          const chart = chartWrapper.getChart();
          const selection = chart.getSelection();

          if (selection.length === 1) {
            const [selectedItem] = selection;
            const { row } = selectedItem;

            switch (row) {
              case 0:
                setDrilldown('tickets');
                return;
              case 1:
                setDrilldown('project_ceremonies');
                return;

              case 2:
                setDrilldown('meetings');
                return;

              default:
                setDrilldown(null);
                return;
            }
          }

          setDrilldown(null);
        },
      },
    ];

    if (drilldown) {
      drilldownTable = (
        <TimeDrilldown
          user={user}
          squad={squad}
          group={group}
          start={start}
          end={end}
          type={drilldown}
        />
      );
    }
  }

  let peopleTab;

  if (showPeople) {
    peopleTab = (
      <Tab eventKey="people" title="People">
        <table className="table">
          <tbody>
            {data.users.map((thisUser) => (
              <tr key={thisUser.id}>
                <th style={{ width: 1, whiteSpace: 'nowrap' }}>
                  <a target="_blank" href={`/dash/user/${thisUser.id}?start=${start}&end=${end}`} rel="noreferrer">{thisUser.name}</a>
                </th>
                <td><UserTimeStackedBarChart user={thisUser.id} start={start} end={end} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Tab>
    );
  }

  if (!showTable) {
    return (
      <Chart
        width="100%"
        height="400px"
        chartType="PieChart"
        data={chartData}
        options={chartOptions}
        chartEvents={chartEvents}
      />
    );
  }

  return (
    <>
      <div className="row">
        <div className="col-12 col-md-6">
          <Chart
            width="100%"
            height="400px"
            chartType="PieChart"
            data={chartData}
            options={chartOptions}
            chartEvents={chartEvents}
          />
        </div>

        <div className="col-12 col-md-6">
          <Tabs defaultActiveKey="breakdown" id="drilldown-tables" mountOnEnter>
            <Tab eventKey="breakdown" title="Breakdown">
              <table className="table mb-4">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Current</th>
                    <th>Previous</th>
                    <th>Diff</th>
                    <th>% Diff</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th>Total</th>
                    <td>
                      {data.current.total}
                      h
                    </td>
                    <td>
                      {data.previous.total}
                      h
                    </td>
                    <td><Diff diff={data.diff.total} /></td>
                    <td />
                  </tr>
                  <tr>
                    <th>Tickets</th>
                    <td>
                      {data.current.tickets}
                      h
                      {' '}
                      <small>
                        (
                        {data.current.percentages.tickets}
                        %)
                      </small>
                    </td>
                    <td>
                      {data.previous.tickets}
                      h
                      {' '}
                      <small>
                        (
                        {data.previous.percentages.tickets}
                        %)
                      </small>
                    </td>
                    <td><Diff diff={data.diff.tickets} /></td>
                    <td><Diff diff={data.percent_diff.tickets} /></td>
                  </tr>
                  <tr>
                    <th>Project Ceremonies</th>
                    <td>
                      {data.current.project_ceremonies}
                      h
                      {' '}
                      <small>
                        (
                        {data.current.percentages.project_ceremonies}
                        %)
                      </small>
                    </td>
                    <td>
                      {data.previous.project_ceremonies}
                      h
                      {' '}
                      <small>
                        (
                        {data.previous.percentages.project_ceremonies}
                        %)
                      </small>
                    </td>
                    <td><Diff diff={data.diff.project_ceremonies} /></td>
                    <td><Diff diff={data.percent_diff.project_ceremonies} /></td>
                  </tr>
                  <tr>
                    <th>Meetings</th>
                    <td>
                      {data.current.meetings}
                      h
                      {' '}
                      <small>
                        (
                        {data.current.percentages.meetings}
                        %)
                      </small>
                    </td>
                    <td>
                      {data.previous.meetings}
                      h
                      {' '}
                      <small>
                        (
                        {data.previous.percentages.meetings}
                        %)
                      </small>
                    </td>
                    <td><Diff diff={data.diff.meetings} /></td>
                    <td><Diff diff={data.percent_diff.meetings} /></td>
                  </tr>
                  <tr>
                    <th>Project Activity</th>
                    <td>
                      {data.current.other_activity}
                      h
                      {' '}
                      <small>
                        (
                        {data.current.percentages.other_activity}
                        %)
                      </small>
                    </td>
                    <td>
                      {data.previous.other_activity}
                      h
                      {' '}
                      <small>
                        (
                        {data.previous.percentages.other_activity}
                        %)
                      </small>
                    </td>
                    <td><Diff diff={data.diff.other_activity} /></td>
                    <td><Diff diff={data.percent_diff.other_activity} /></td>
                  </tr>
                  <tr>
                    <th>Untracked</th>
                    <td>
                      {data.current.untracked}
                      h
                      {' '}
                      <small>
                        (
                        {data.current.percentages.untracked}
                        %)
                      </small>
                    </td>
                    <td>
                      {data.previous.untracked}
                      h
                      {' '}
                      <small>
                        (
                        {data.previous.percentages.untracked}
                        %)
                      </small>
                    </td>
                    <td><Diff diff={data.diff.untracked} /></td>
                    <td><Diff diff={data.percent_diff.untracked} /></td>
                  </tr>
                </tbody>
              </table>
            </Tab>
            {peopleTab}
          </Tabs>

        </div>
      </div>

      <div className="row">
        <div className="col-md-12">
          {drilldownTable}
        </div>
      </div>
    </>
  );
}

export function UserTimeStackedBarChart({ user, start, end }) {
  const [data, setData] = useState();

  const loadData = async () => {
    try {
      const response = await axios.get('/dash/time-data', {
        params: {
          user_id: user,
          start,
          end,
        },
        paramsSerializer: (params) => qs.stringify(params),
      });

      setData(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => { setData(null); loadData(); }, [user, start, end]);

  if (!data || data.length === 0) {
    return <Spinner animation="border" role="status" variant="info" size="xs" />;
  }

  let total = data.current.percentages.tickets
    + data.current.percentages.project_ceremonies
    + data.current.percentages.meetings
    + data.current.percentages.other_activity
    + data.current.percentages.untracked;

  if (total === 0) {
    total = 1;
  }

  const percentages = {
    tickets: (100 / total) * data.current.percentages.tickets,
    project_ceremonies: (100 / total) * data.current.percentages.project_ceremonies,
    meetings: (100 / total) * data.current.percentages.meetings,
    other_activity: (100 / total) * data.current.percentages.other_activity,
    untracked: (100 / total) * data.current.percentages.untracked,
  };

  let untrackedColor = '#cccccc';

  if (data.current.total === 0) {
    untrackedColor = 'darkred';
  }

  return (
    <div style={{ position: 'relative', clear: 'both' }}>
      <div
        title={`Tickets: ${percentages.tickets.toFixed(1)}%`}
        style={{
          width: `${percentages.tickets}%`, height: '15px', background: '#38c172', float: 'left',
        }}
      />
      <div
        title={`Project Ceremonies: ${percentages.project_ceremonies.toFixed(1)}%`}
        style={{
          width: `${percentages.project_ceremonies}%`, height: '15px', background: '#4dc0b5', float: 'left',
        }}
      />
      <div
        title={`Meetings: ${percentages.meetings.toFixed(1)}%`}
        style={{
          width: `${percentages.meetings}%`, height: '15px', background: '#3490dc', float: 'left',
        }}
      />
      <div
        title={`Project Activity: ${percentages.other_activity.toFixed(1)}%`}
        style={{
          width: `${percentages.other_activity}%`, height: '15px', background: '#85A7C4', float: 'left',
        }}
      />
      <div
        title={`Untracked: ${percentages.untracked.toFixed(1)}%`}
        style={{
          width: `${percentages.untracked}%`, height: '15px', background: untrackedColor, float: 'left',
        }}
      />
    </div>
  );
}

function TimeDrilldown({
  user, squad, group, start, end, type,
}) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState();

  const loadData = async () => {
    setLoading(true);

    try {
      const response = await axios.get('/dash/time-drilldown', {
        params: {
          user_id: user,
          squad_id: squad,
          group_id: group,
          start,
          end,
          type,
        },
        paramsSerializer: (params) => qs.stringify(params),
      });

      setData({ type, time: response.data });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setData(null); loadData(); }, [squad, group, start, end, type]);

  if (loading || !data || data.length === 0) {
    return (
      <div style={{ height: 150 }}>
        <LoadingAnimation loading={loading} />
      </div>
    );
  }

  if (data.type && data.type === 'tickets') {
    return (
      <table className="table">
        <thead>
          <tr>
            <th>User</th>
            <th>Project</th>
            <th>Ticket</th>
            <th>Time</th>
          </tr>
        </thead>
        {data.time.map((row) => (
          <tr>
            <td>{row.user_name}</td>
            <td><a href={`/projects/${row.project_id}`} title={row.project_title}>{row.project_code}</a></td>
            <td title={row.ticket_title}>{row.ticket_title.length > 50 ? `${row.ticket_title.substring(0, 47)}...` : row.ticket_title}</td>
            <td>{row.duration_str}</td>
          </tr>
        ))}
      </table>
    );
  }

  if (data.type && data.type === 'project_ceremonies') {
    return (
      <table className="table">
        <thead>
          <tr>
            <th>User</th>
            <th>Project</th>
            <th>Type</th>
            <th>Time</th>
          </tr>
        </thead>
        {data.time.map((row) => (
          <tr>
            <td>{row.user_name}</td>
            <td><a href={`/projects/${row.project_id}`} title={row.project_title}>{row.project_code}</a></td>
            <td>{row.meeting_type}</td>
            <td>{row.duration_str}</td>
          </tr>
        ))}
      </table>
    );
  }

  if (data.type && data.type === 'meetings') {
    return (
      <table className="table">
        <thead>
          <tr>
            <th>User</th>
            <th>Meeting</th>
            <th>Time</th>
          </tr>
        </thead>
        {data.time.map((row) => (
          <tr>
            <td>{row.user_name}</td>
            <td>{row.meeting}</td>
            <td>{row.duration_str}</td>
          </tr>
        ))}
      </table>
    );
  }

  return 'Something went wrong.';
}

function Diff({ diff }) {
  let icon = 'equals';
  let color = 'text-warning';

  if (diff > 0) {
    icon = 'angle-double-up';
    color = 'text-success';
  }

  if (diff < 0) {
    icon = 'angle-double-down';
    color = 'text-danger';
  }

  return (
    <div className={color} style={{ whiteSpace: 'nowrap' }}>
      <Icon icon={icon} variant="fad" append="mr-1" />
      {' '}
      {diff}
      %
    </div>
  );
}

export default WeeklyReport;
